import React, { useState } from 'react';
import { useSpring, config, animated } from 'react-spring';
import hexToRgba from 'hex-to-rgba';
import PropTypes from 'prop-types';
import Container from '../components/Container';
import Icon from '../components/Icon';

import './About.scss';

const CodeIcon = () => <i className="fa-solid fa-code"></i>;
const PenRulerIcon = () => <i className="fa-solid fa-pen-ruler"></i>;
const SchoolIcon = () => <i className="fa-solid fa-school"></i>;
const CertIcon = () => <i className="fa-solid fa-certificate"></i>;

const Card = ({ icon, children, ...props }) => {
  const [isHover, setIsHover] = useState(false);
  const zoomStyles = useSpring({
    config: { ...config.stiff },
    from: { scale: 1 },
    to: { scale: isHover ? 1.03 : 1 },
  });

  return (
    <animated.div
      className="card shadow-on-hover p-4 mb-2"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={{ ...zoomStyles }}
      {...props}
    >
      {children}
    </animated.div>
  );
};

const CardTitle = ({ icon, text, subText }) => (
  <div className="d-flex justify-content-between">
    <div className="mb-2">
      <p
        className="fw-normal lead mb-0"
        style={{ color: 'var(--bs-teal)', lineHeight: 1 }}
      >
        {text}
      </p>
      {subText && <span className="sub">{subText}</span>}
    </div>
    <div className="card-icon">{icon}</div>
  </div>
);

const CardDescription = ({ text }) => (
  <small className="text-gray">{text}</small>
);

function About({ id, data, ...props }) {
  return (
    <section id={`${id}-section`} {...props}>
      <Container
        id={id}
        title="ABOUT"
        titleClassName="underline fw-bold"
        headerClassName="mb-5"
      >
        <div className="row g-1">
          <div
            className="col-12 col-md-6 px-md-5 mx-auto"
            style={{ maxWidth: '600px' }}
          >
            <div className="mb-5">
              <sup>Introduce</sup>
              <h1 className="display-6 mb-3">
                Hi! I'm{' '}
                <span className="fw-normal" style={{ color: 'var(--bs-teal)' }}>
                  Dickson Palomeras
                </span>
              </h1>
              <p className="text-gray">
                An aspiring <span className="text-white">web developer</span>{' '}
                based in philippines. Since the beginning of my journey as a
                front end eng'r , I've done so many work I created successful
                responsive websites that are fast, easy to use, and built with
                best practices. I'm quietly confident, naturally curious, and
                perpetually working on improving my chops one problem at a time.
              </p>
            </div>
            <div className="mb-4">
              <sup>Main Skills</sup>
              <Card>
                <CardTitle text="Web Development" icon={<CodeIcon />} />
                <CardDescription
                  text="Build websites and web apps using javascript and it's
                frameworks."
                />
              </Card>
              <Card>
                <CardTitle
                  text="Web and user interface design"
                  icon={<PenRulerIcon />}
                />
                <CardDescription text="Websites, User Experience, Visual Design" />
              </Card>
            </div>
            <div className="mb-4">
              <sup>Educations & Certifications</sup>
              <Card>
                <CardTitle
                  text="BS in Information Technology"
                  subText="Major in Information System"
                  icon={<SchoolIcon />}
                />
                <CardDescription text="Partido State University - Goa, Camarines Sur" />
              </Card>
              <Card>
                <CardTitle
                  text="MINDTECH"
                  subText="Training and Development Institute Inc."
                  icon={<CertIcon />}
                />
                <CardDescription text="Rosario, Pasig City" />
              </Card>
            </div>
          </div>
          <div
            className="col-12 col-md-6 px-md-5 mx-auto"
            style={{ maxWidth: '600px' }}
          >
            <sup>Technologies</sup>
            <div className="d-none d-md-block">
              <div className="row gy-4 py-1">
                {data.map((lang, idx) => (
                  <div key={idx} className="col-md-4 col-lg-3 col-xl-2 px-0">
                    <div className="text-center tech-hover">
                      <div className="display-6">
                        <Icon
                          type={lang.type}
                          icon={lang.icon}
                          className="colored"
                        />
                      </div>
                      <small>{lang.name}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="d-block d-md-none">
              <div className="tags py-1">
                {data.map((t, idx) => (
                  <span
                    key={idx}
                    className="badge rounded-pill m-1"
                    style={{
                      color: t.color,
                      background: hexToRgba(t.color, 0.1),
                      border: `1px solid ${hexToRgba(t.color, 0.3)}`,
                    }}
                  >
                    <Icon type={t.type} icon={t.icon} className="colored" />
                    &nbsp;{t.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

About.defaultProps = {
  id: 'about',
};

About.propTypes = {
  id: PropTypes.string,
};

export default React.memo(About, (prev, next) => true);
